import React from 'react';
import Decimal from 'decimal.js';
import { roundTo3Decimals } from 'src/utils/numberHelper';
import { getStowageItemShipFigure } from 'src/utils/stowage/stowageOperation/operationFormatUtils';
import { OperationWithLocation } from 'src/utils/stowage/stowageOperation/operationFormatUtils';
import { Stowage } from 'src/types/ViewModel/Stowage/Stowage';
import { OperationsTableRow, OperationsTableCell } from './OperationsForCargoPanel.styles';

interface StowageItemsRowProps {
    item: OperationWithLocation;
    columnCount: number;
    stowageItems: Stowage[];
}

/**
 * Component for rendering stowage items (tanks) within an operation
 * Shows individual tank allocations with proportional weights and volumes
 */
const StowageItemsRow: React.FC<StowageItemsRowProps> = ({ item, columnCount, stowageItems }) => {
    // If no stowage items, show placeholder row
    if (stowageItems.length === 0) {
        return (
            <OperationsTableRow key={`stowage-${item.operation.cargoOperationGuid}`}>
                <OperationsTableCell style={{ fontStyle: 'italic', color: '#666', padding: '8px 16px' }}>No stowage items</OperationsTableCell>
                {Array.from({ length: columnCount - 1 }, (_, index) => (
                    <OperationsTableCell key={index} style={{ color: '#666' }}>
                        -
                    </OperationsTableCell>
                ))}
            </OperationsTableRow>
        );
    }

    // Render each stowage item (tank) as a row
    return (
        <>
            {stowageItems.map((stowageItem, index) => {
                // Calculate nominated weight for this stowage item (proportional to its weight)
                const totalStowedWeight = item.stowedWeight.toNumber();
                const stowageItemWeight = stowageItem.weight.toNumber();
                const nomWeightProportion = totalStowedWeight > 0 ? stowageItemWeight / totalStowedWeight : 0;
                const nomWeight = item.operation.nominatedWeight ? item.operation.nominatedWeight * nomWeightProportion : 0;

                // Calculate BL weight proportionally
                const blWeight = item.operation.billOfLadingWeight ? item.operation.billOfLadingWeight * nomWeightProportion : 0;

                // Calculate not stowed weight (only for loading operations)
                // TODO: MARITIME-BUSINESS-RULE - Verify proportional calculation logic for not stowed weight
                const notStowedWeight =
                    item.operation.operationType === 'Loading' && item.notStowedWeight instanceof Decimal
                        ? item.notStowedWeight.toNumber() * nomWeightProportion
                        : 0;

                return (
                    <OperationsTableRow key={`stowage-${item.operation.cargoOperationGuid}-${index}`}>
                        <OperationsTableCell style={{ paddingLeft: '32px', fontSize: '0.9em', color: '#666' }}></OperationsTableCell>
                        <OperationsTableCell style={{ fontSize: '0.9em', color: '#666' }}>
                            {stowageItem.tank?.tankName || 'Unknown Tank'}
                        </OperationsTableCell>
                        <OperationsTableCell style={{ fontSize: '0.9em', color: '#666' }}>
                            {nomWeight > 0 ? `${roundTo3Decimals(nomWeight)} MT` : '-'}
                        </OperationsTableCell>
                        <OperationsTableCell style={{ fontSize: '0.9em', color: '#666' }}>
                            {blWeight > 0 ? `${roundTo3Decimals(blWeight)} MT` : '-'}
                        </OperationsTableCell>
                        <OperationsTableCell style={{ fontSize: '0.9em', color: '#666' }}>
                            {getStowageItemShipFigure(stowageItem)}
                        </OperationsTableCell>
                        <OperationsTableCell style={{ fontSize: '0.9em', color: '#666' }}>
                            {roundTo3Decimals(stowageItem.weight.toNumber())} MT
                        </OperationsTableCell>
                        <OperationsTableCell style={{ fontSize: '0.9em', color: '#666' }}>
                            {item.operation.operationType === 'Loading' && notStowedWeight > 0 ? `${roundTo3Decimals(notStowedWeight)} MT` : '-'}
                        </OperationsTableCell>
                    </OperationsTableRow>
                );
            })}
        </>
    );
};

export default StowageItemsRow;
